/* eslint-disable @typescript-eslint/no-explicit-any */
import { UserZod } from '../../../environment/ZodTypes';
import { AuthService } from '../auth/AuthService';
import { ApiBack } from '../axios-config';

export type PermissaoTipo = 'add' | 'edit' | 'remove' | 'view';

export interface SubNivelAcesso {
	central?: string;
	identificacao?: string;
	caracterizacao?: string;
	organograma?: string;
	caracteristicas?: string;
	arquivos?: string;
	RISR?: string;
	RISROT?: string;
	RISE?: string;
	operacoes?: string;
	monitoramento?: string;
	comunicacao?: string;
	visualizacao?: string;
	inspecoes?: string;
	anomalias?: string;
	PAE?: string;
	plano?: string;
	RPSB?: string;
	conformidades?: string;
	treinamento?: string;
	seminario?: string;
	simulado?: string;
	registro?: string;
	dashboardGeral?: string;
	leitura?: string;
	novo_risrot?: string;
	nova_anomalia?: string;
	inicio_atividade?: string;
	novo_plano_acao?: string;
	status_plano?: string;
	status_tarefa?: string;
	nova_tarefa?: string;
	novo_topico?: string;
}

export interface NivelAcesso {
	informacoes?: SubNivelAcesso;
	documentacao_tecnica?: SubNivelAcesso;
	registros?: SubNivelAcesso;
	acompanhamentos?: SubNivelAcesso;
	revisao?: SubNivelAcesso;
	PAE?: SubNivelAcesso;
	dashboard?: SubNivelAcesso;
	notificacao?: SubNivelAcesso;
}

export interface IUser {
	login: string;
	senha?: string;
	nome: string;
	email: string;
	notificacao?: boolean;
	nivel?: string;
	ativo?: boolean;
	main_user?: boolean;
	id_empresa?: string;
	permissoes: string;
	foto?: string;
	register?: string;
	ramal?: string;
	mobile_phone?: string;
	role?: string;
	sector?: string;
	uuid?: string;
	empresa?: string;
	outsystem?: boolean;
	nivel_acesso?: NivelAcesso;
	barragens_visiveis?: string[] | string;
}

const get = async (
	uuid: string | null | undefined
): Promise<IUser[] | Error> => {
	await AuthService.verify();

	try {
		const token = await AuthService.getAccessToken();

		const response = await ApiBack.get(`./usuarios?uuid=${uuid}`, {
			headers: {
				Authorization: `${token}`,
			},
		});

		if (response.data) {
			return response.data;
		}

		return new Error('Erro ao listar usuários.');
	} catch (error) {
		console.error(error);
		return new Error(
			(error as { message: string }).message ||
			'Erro na requisição de listagem.'
		);
	}
};

const getById = async (uuid: string): Promise<IUser | Error> => {
	await AuthService.verify();

	try {
		const token = await AuthService.getAccessToken();

		const response = await ApiBack.get('./usuario', {
			params: {
				uuid: uuid,
			},
			headers: {
				Authorization: `${token}`,
			},
		});

		if (response.data) {
			return response.data;
		}

		return new Error('Erro ao buscar usuário');
	} catch (error) {
		return new Error(
			(error as { message: string }).message || 'Erro ao buscar usuário'
		);
	}
};

const getByIdZod = async (uuid: string): Promise<UserZod | Error> => {
	await AuthService.verify();

	try {
		const token = await AuthService.getAccessToken();

		const response = await ApiBack.get('./usuario', {
			params: {
				uuid: uuid,
			},
			headers: {
				Authorization: `${token}`,
			},
		});

		if (response.data) {
			return response.data;
		}

		return new Error('Erro ao buscar usuário');
	} catch (error) {
		return new Error(
			(error as { message: string }).message || 'Erro ao buscar usuário'
		);
	}
};

const create = async (uuid: string, data: IUser): Promise<IUser | Error> => {
	await AuthService.verify();

	try {
		const token = await AuthService.getAccessToken();

		const response = await ApiBack.post(`./usuario?uuid=${uuid}`, data, {
			headers: {
				Authorization: `${token}`,
			},
		});

		if (response.data) {
			return response.data;
		}

		return new Error('Erro ao criar novo usuário');
	} catch (error: any) {
		if (
			error.response &&
			error.response.data &&
			error.response.data.error &&
			error.response.data.message
		) {
			return new Error(error.response.data.message);
		} else {
			return new Error(
				(error as { message: string }).message || 'Erro ao criar novo usuário'
			);
		}
	}
};

const createResponsible = async (
	uuid: string,
	data: UserZod
): Promise<IUser | Error> => {
	await AuthService.verify();

	try {
		const token = await AuthService.getAccessToken();

		const response = await ApiBack.post(`./usuario?uuid=${uuid}`, data, {
			headers: {
				Authorization: `${token}`,
			},
		});

		if (response.data) {
			return response.data;
		}

		return new Error('Erro ao criar novo usuário');
	} catch (error: any) {
		if (
			error.response &&
			error.response.data &&
			error.response.data.error &&
			error.response.data.message
		) {
			return new Error(error.response.data.message);
		} else {
			return new Error(
				(error as { message: string }).message || 'Erro ao criar novo usuário'
			);
		}
	}
};

const upgrade = async (uuid: string, data: IUser): Promise<any | Error> => {
	await AuthService.verify();

	try {
		const token = await AuthService.getAccessToken();

		const response = await ApiBack.put(`./usuario?uuid=${uuid}`, data, {
			headers: {
				Authorization: `${token}`,
			},
		});

		if (response.data) {
			return response.data;
		}

		return new Error('Falha ao atualizar usuário');
	} catch (error) {
		return new Error(
			(error as { menssage: string }).menssage || 'Error ao atualizar usuário'
		);
	}
};

const upgradeResponsible = async (
	uuid: string,
	data: UserZod
): Promise<any | Error> => {
	await AuthService.verify();

	try {
		const token = await AuthService.getAccessToken();

		const response = await ApiBack.put(`./usuario?uuid=${uuid}`, data, {
			headers: {
				Authorization: `${token}`,
			},
		});

		if (response.data) {
			return response.data;
		}

		return new Error('Falha ao atualizar usuário');
	} catch (error) {
		return new Error(
			(error as { menssage: string }).menssage || 'Error ao atualizar usuário'
		);
	}
};

const deleteById = async (uuid: string): Promise<void | Error> => {
	await AuthService.verify();

	try {
		const token = await AuthService.getAccessToken();

		await ApiBack.delete(`/usuario?uuid=${uuid}`, {
			headers: {
				Authorization: `${token}`,
			},
		});
	} catch (error) {
		console.error(error);
		return new Error(
			(error as { message: string }).message || 'Erro ao deletar registro.'
		);
	}
};

export const UserServices = {
	get,
	create,
	deleteById,
	upgrade,
	getById,
	createResponsible,
	upgradeResponsible,
	getByIdZod,
};
